import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { checkNewTags, TagValidationResult } from '@/services/tagValidationService';

export interface UseTagValidationReturn {
  validateTags: (tags: string[]) => Promise<TagValidationResult>;
  isValidating: boolean;
  validationError: string | null;
  clearError: () => void;
}

/**
 * Hook for validating tags against the database
 * Returns which tags are new vs existing
 */
export const useTagValidation = (): UseTagValidationReturn => {
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const validateTags = useCallback(async (tags: string[]): Promise<TagValidationResult> => {
    setIsValidating(true);
    setValidationError(null);
    
    try {
      const result = await checkNewTags(supabase, tags);
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to validate tags';
      setValidationError(errorMessage);
      console.error('Tag validation error:', error);
      
      // Return safe default when validation fails
      return {
        existingTags: [],
        newTags: tags, // Assume all tags are new when validation fails
        hasNewTags: tags.length > 0
      };
    } finally {
      setIsValidating(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setValidationError(null);
  }, []);

  return {
    validateTags,
    isValidating,
    validationError,
    clearError
  };
}; 